import { ChangeDetectionStrategy, Component, linkedSignal, signal, inject, computed, effect } from '@angular/core';
import { ToastService } from 'src/app/services/toast.service';
import { BehaviorSubject, first } from 'rxjs';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { AuthService } from 'src/app/services/Auth/auth.service';
import { APIService } from 'src/app/services/APIService/api.service';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ClarityModule } from '@clr/angular';
import { PlotlyPlotComponent } from '../../plotly-plot/plotly-plot.component';
import { ConfirmService } from 'src/app/services/confirm.service';
import { Row, Table } from 'src/app/models/ui/table';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { ExcelService } from 'src/app/services/Shared/excel.service';
import { TablePrettyPrintPipe } from '../../../../pipes/tablePrettyPrint.pipe';

interface KAVState {
  ebene: number;
  kavTitel: string;
  diagram: any[];
  table: Table;
  gesKAV: any;
  trace: any;
  auswahl: number;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-kavdetaildialog',
  templateUrl: './kavdetaildialog.component.html',
  styleUrl: './kavdetaildialog.component.scss',
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    PlotlyPlotComponent,
    MrTranslatePipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
  ],
})
export class KavdetaildialogComponent {
  private apiservice = inject(APIService);
  private mrTranslate = inject(MrTranslatePipe);
  authService = inject(AuthService);
  private toastr = inject(ToastService);
  private confirmService = inject(ConfirmService);
  private excelService = inject(ExcelService);

  public closedialog: BehaviorSubject<boolean> = new BehaviorSubject(false);

  protected readonly auswahl = signal([
    { key: 5, value: 'Anlagenklasse' },
    { key: 1, value: 'Bezirk' },
    { key: 4, value: 'Erneuerungsbedarf Gleis' },
    { key: 3, value: 'Erneuerungsbedarf Weichen' },
    { key: 7, value: 'Eigentümer' },
    { key: 0, value: 'KAV Entwicklung' },
    { key: 2, value: 'Objekttyp' },
    { key: 8, value: 'Ort' },
    { key: 6, value: 'Stellwerk' },
    { key: 9, value: 'Verantwortlich' },
    { key: 10, value: 'Qualitätsindex' },
  ]);

  protected readonly diagramm = signal<Record<number, any[]>>({
    0: [],
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
    6: [],
    7: [],
    8: [],
    9: [],
    10: [],
  });
  private apiService = inject(APIService);
  protected readonly curDiagramm = computed(() => this.diagramm()[this.kavansicht()]);
  protected readonly kavansicht = signal<any>(0);
  protected readonly typansicht = signal<any>(0);
  protected readonly tableW = signal<Table>({ rows: [], columns: [] });
  protected readonly tableG = signal<Table>({ rows: [], columns: [] });
  protected readonly selectedRow = signal<Row>(undefined);
  protected readonly ostammid = linkedSignal(() => +this.selectedRow()?.OSTAMMID);
  protected readonly curtable = signal<Table>({ rows: [], columns: [] });

  history: KAVState[] = [];

  lasttrace: any;
  graph: any;
  graphkav0: any;
  graphkav1: any;
  graphkav2: any;
  graphkav3: any;

  curgraphkav: any;
  kav_erneu_weiche: any = [];
  kav_erneu_gleis: any = [];
  kavTitel: string = '';
  gesKAV: any;
  ebene: number = 0;

  set data(In: any) {
    this.graph = In.data.graph;
    this.graphkav0 = In.data.graphkav0;
    this.graphkav1 = In.data.graphkav1;
    this.graphkav2 = In.data.graphkav2;
    this.graphkav3 = In.data.graphkav3;
    this.diagramm.set(In.data.diagramm);
    this.curgraphkav = this.graphkav0;
    this.kavansicht.set(In.data.index);
    this.kav_erneu_weiche = In.data.kav_erneu_weiche;
    this.kav_erneu_gleis = In.data.kav_erneu_gleis;
    this.gesKAV = In.data.gesKAV;
    this.tableW.set({ ...In.data.dtWRewNewdates! });
    this.tableG.set({ ...In.data.dtGRewNewdates! });
    this.kavTitel = this.mrTranslate.transform('KAV Entwicklung der letzten 5 Jahre');
  }

  constructor() {
    const keys = Object.keys(this.auswahl());
    this.kavansicht.set(keys.length ? +keys[0] : 0);
  }

  get kavansichtValue(): number {
    return this.kavansicht();
  }

  set kavansichtValue(val: number) {
    this.kavansicht.set(val);
    this.changeKAVStatus();
    this.ebene = 0;
    this.history = [];
  }

  excelexport() {
    this.excelService.exportAsExcelFile(this.curtable().rows, this.kavTitel);
  }

  goBack() {
    if (this.history.length === 0) {
      return;
    }

    const previous = this.history.pop()!;

    this.ebene = previous.ebene;
    this.kavTitel = previous.kavTitel;
    this.gesKAV = previous.gesKAV;
    this.lasttrace = previous.trace;

    this.diagramm.update(old => ({
      ...old,
      [this.kavansicht()]: previous.diagram,
    }));

    this.curtable.set(previous.table);

    if (this.ebene == 0) {
      this.kavansicht.set(previous.auswahl);
    }
  }

  onBarClick(event: any) {
    //console.log('Clicked bar:', event);

    this.history.push({
      ebene: this.ebene,
      kavTitel: this.kavTitel,
      diagram: [...this.curDiagramm()],
      table: this.curtable(),
      gesKAV: this.gesKAV,
      trace: this.lasttrace,
      auswahl: this.kavansicht(),
    });

    if (this.curgraphkav == this.graphkav1) {
      const point = event.points[0];

      console.log('Category:', point.x);
      console.log('Value:', point.y);
      console.log('Index:', point.pointIndex);

      const kavansicht = this.kavansicht();
      let id = 0;

      let field = 'Bezirk';
      if (this.ebene == 0) {
        field = this.auswahl().find(item => item.key === this.kavansicht())?.value;
      } else {
        id = this.lasttrace['id'][point.pointIndex];
      }
      this.ebene += 1;

      this.kavTitel = point.x + ': ' + point.y;

      this.apiService
        .getStatistikKAVDetail(this.ebene, field, point.x, id)
        .pipe(first())
        .subscribe(res => {
          if (res['success'] == true) {
            this.gesKAV = point.y;
            this.kavansicht.set(this.kavansicht() + 1000 + this.ebene);
            this.diagramm.update(old => ({
              ...old,
              [this.kavansicht()]: res['data'],
            }));

            this.curgraphkav = this.graphkav1;
            const trace = res['data'][0];

            this.lasttrace = res['data'][0];

            this.curtable.set({
              columns: [
                { bezeichnung: 'Bezeichnung', id: 'Bezeichnung' },
                { bezeichnung: 'Werte', id: 'Werte' },
              ],
              rows: trace.x.map((bezeichnung: string, index: number) => ({
                Bezeichnung: this.mrTranslate.transform(bezeichnung),
                Werte: trace.y[index],
              })),
            });

            let s = 'stop';
          } else {
            this.toastr.warning(this.mrTranslate.transform('Ein Fehler ist aufgetreten'));
          }
        });
    }
  }

  changeKAVStatus() {
    this.typansicht.set(0);
    switch (this.kavansicht()) {
      case 0:
        this.kavTitel = this.mrTranslate.transform('KAV Entwicklung der letzten 5 Jahre');
        this.curgraphkav = this.graphkav0;
        break;
      case 1:
        this.kavTitel = this.mrTranslate.transform('Bezirk') + ': ' + this.gesKAV;
        this.curgraphkav = this.graphkav1;
        break;
      case 2:
        this.kavTitel = this.mrTranslate.transform('Objekttyp') + ': ' + this.gesKAV;
        this.curgraphkav = this.graphkav1;
        break;
      case 3:
        this.kavTitel = this.mrTranslate.transform('Erneuerungsbedarf Weichen');
        this.curgraphkav = this.graphkav2;
        break;
      case 4:
        this.kavTitel = this.mrTranslate.transform('Erneuerungsbedarf Gleis');
        this.curgraphkav = this.graphkav3;
        break;
      case 5:
        this.kavTitel = this.mrTranslate.transform('Anlagenklasse') + ': ' + this.gesKAV;
        this.curgraphkav = this.graphkav1;
        break;
      case 6:
        this.kavTitel = this.mrTranslate.transform('Stellwerk') + ': ' + this.gesKAV;
        this.curgraphkav = this.graphkav1;
        break;
      case 7:
        this.kavTitel = this.mrTranslate.transform('Eigentümer') + ': ' + this.gesKAV;
        this.curgraphkav = this.graphkav1;
        break;
      case 8:
        this.kavTitel = this.mrTranslate.transform('Ort') + ': ' + this.gesKAV;
        this.curgraphkav = this.graphkav1;
        break;
      case 9:
        this.kavTitel = this.mrTranslate.transform('Verantwortlich') + ': ' + this.gesKAV;
        this.curgraphkav = this.graphkav1;
        break;
      case 10:
        this.kavTitel = '';
        this.curgraphkav = this.graph;
        break;
    }
  }

  changeView() {
    if (this.typansicht() == 0) {
      if (this.kavansicht() == 3) {
        this.curtable.set(this.tableW());
      } else if (this.kavansicht() == 4) {
        this.curtable.set(this.tableG());
      } else {
        const diagram = this.diagramm()[this.kavansicht()];
        const trace = diagram[0];
        if (trace.labels.length > 0) {
          this.curtable.set({
            columns: [
              { bezeichnung: 'Bezeichnung', id: 'Bezeichnung' },
              { bezeichnung: 'Werte', id: 'Werte' },
            ],
            rows: trace.labels.map((bezeichnung: string, index: number) => ({
              Bezeichnung: bezeichnung,
              Werte: trace.values[index],
            })),
          });
        } else {
          this.curtable.set({
            columns: [
              { bezeichnung: 'Bezeichnung', id: 'Bezeichnung' },
              { bezeichnung: 'Werte', id: 'Werte' },
            ],
            rows: trace.x.map((bezeichnung: string, index: number) => ({
              Bezeichnung: bezeichnung,
              Werte: trace.y[index],
            })),
          });
        }
      }

      this.typansicht.set(1);
    } else this.typansicht.set(0);
  }

  close() {
    this.closedialog.next(true);
  }
}
